import React, { useState, useEffect, useRef } from "react";
import { useWorldClocks } from "../hooks/useWorldClocks";
import ClockDisplay from "./ClockDisplay";
// import MainActionButton from './MainActionButton'; // No longer using MainActionButton for the '+'
// import { lang } from '../lang'; // lang.app.addClock is not used for '+'
import "./ClockManager.css";

// A simplified list of IANA timezones with friendly names
const commonTimezones = [
  { name: "UTC", friendlyName: "UTC" },
  { name: "Europe/London", friendlyName: "London" },
  { name: "Europe/Paris", friendlyName: "Paris" },
  { name: "Europe/Berlin", friendlyName: "Berlin" },
  { name: "America/New_York", friendlyName: "New York" },
  { name: "America/Chicago", friendlyName: "Chicago" },
  { name: "America/Denver", friendlyName: "Denver" },
  { name: "America/Los_Angeles", friendlyName: "Los Angeles" },
  { name: "Asia/Tokyo", friendlyName: "Tokyo" },
  { name: "Asia/Dubai", friendlyName: "Dubai" },
  { name: "Asia/Kolkata", friendlyName: "Kolkata" },
  { name: "Australia/Sydney", friendlyName: "Sydney" },
  { name: "Pacific/Auckland", friendlyName: "Auckland" },
];

function ClockManager({ settings }) {
  const { clocks, addClock, removeClock, updateClock, MAX_ADDITIONAL_CLOCKS } =
    useWorldClocks();
  const [showAddClockDropdown, setShowAddClockDropdown] = useState(false);

  const [newClockUserInputName, setNewClockUserInputName] = useState("");
  const [newClockIanaTimezone, setNewClockIanaTimezone] = useState(
    commonTimezones[0]?.name || "UTC"
  );

  const [currentGlobalTime, setCurrentGlobalTime] = useState(new Date());
  const addClockControlsRef = useRef(null); // Ref for the add clock controls area

  useEffect(() => {
    const timerId = setInterval(() => {
      setCurrentGlobalTime(new Date());
    }, 1000);
    return () => clearInterval(timerId);
  }, []);

  // Click outside handler for add clock dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      // Check if the click is outside the addClockControlsRef and not on the toggle button itself
      if (
        addClockControlsRef.current &&
        !addClockControlsRef.current.contains(event.target) &&
        !event.target.closest(".plus-add-clock-button")
      ) {
        // Check if click was on the '+' button
        setShowAddClockDropdown(false);
      }
    }
    if (showAddClockDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showAddClockDropdown]);

  const localClock = clocks.find((c) => c.isLocal);
  const additionalClocks = clocks.filter((c) => !c.isLocal);

  const handleAddNewClock = () => {
    if (newClockIanaTimezone) {
      addClock(newClockIanaTimezone, newClockUserInputName.trim());
      setShowAddClockDropdown(false);
      setNewClockUserInputName("");
      const firstAvailable = commonTimezones.find(
        (tz) => !clocks.some((c) => c.ianaTimezone === tz.name)
      );
      setNewClockIanaTimezone(
        firstAvailable?.name || commonTimezones[0]?.name || "UTC"
      );
    }
  };

  const canAddMoreClocks = additionalClocks.length < MAX_ADDITIONAL_CLOCKS;

  const getDynamicTimezoneList = () => {
    const localTZEnv = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const list = [...commonTimezones];
    if (localTZEnv && !list.some((tz) => tz.name === localTZEnv)) {
      list.unshift({
        name: localTZEnv,
        friendlyName: `${localTZEnv
          .split("/")
          .pop()
          .replace(/_/g, " ")} (Local)`,
      });
    }
    return list;
  };
  const dynamicTimezoneList = getDynamicTimezoneList();

  const availableTimezonesForAdding = dynamicTimezoneList.filter(
    (tz) => !clocks.some((clock) => clock.ianaTimezone === tz.name)
  );

  if (!settings.showClocks) {
    return null;
  }

  return (
    <div className="clock-manager-container">
      <div className="clocks-wrapper">
        {localClock && (
          <ClockDisplay
            key={localClock.id}
            clock={localClock}
            timeFormat={settings.clockTimeFormat}
            clockFontTheme={settings.clockFontTheme} // Pass font theme
            currentGlobalTime={currentGlobalTime}
            availableTimezones={dynamicTimezoneList}
            onUpdate={updateClock}
          />
        )}
        {additionalClocks.map((clock) => (
          <ClockDisplay
            key={clock.id}
            clock={clock}
            timeFormat={settings.clockTimeFormat}
            clockFontTheme={settings.clockFontTheme} // Pass font theme
            currentGlobalTime={currentGlobalTime}
            onRemove={removeClock}
            onUpdate={updateClock}
            availableTimezones={dynamicTimezoneList}
          />
        ))}
      </div>

      {canAddMoreClocks && (
        <div className="add-clock-controls" ref={addClockControlsRef}>
          <button
            onClick={() => setShowAddClockDropdown((prev) => !prev)}
            className="plus-add-clock-button" // New class for styling the "+" button
            title="Add another world clock"
            aria-label="Add another world clock"
          >
            +
          </button>

          {showAddClockDropdown && (
            <div className="add-clock-dropdown">
              <input
                type="text"
                placeholder="City Name (Optional)"
                value={newClockUserInputName}
                onChange={(e) => setNewClockUserInputName(e.target.value)}
                className="clock-city-input-adder"
              />
              <select
                value={newClockIanaTimezone}
                onChange={(e) => setNewClockIanaTimezone(e.target.value)}
                className="clock-timezone-select-adder"
              >
                {availableTimezonesForAdding.length > 0 ? (
                  availableTimezonesForAdding.map((tz) => (
                    <option key={tz.name} value={tz.name}>
                      {tz.friendlyName}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>
                    All common timezones added
                  </option>
                )}
              </select>
              <button // Changed from MainActionButton to a simple button for styling consistency
                onClick={handleAddNewClock}
                className="confirm-add-clock-button"
                disabled={
                  !newClockIanaTimezone ||
                  availableTimezonesForAdding.length === 0
                }
              >
                Add
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ClockManager;
